import React, { FunctionComponent, useState } from 'react'
import { FieldRenderProps } from 'react-final-form'
import { LayoutChangeEvent, TextProps, View } from 'react-native'

import { useTheme } from 'styled-components/native'

import { CheckboxButtonType } from '@lyrahealth-inc/shared-app-logic/src/models/formMetadata/FormMetadata'

import { getCheckboxButtonWidth, GridCheckboxSize } from './Checkbox'
import { CheckboxGroup, CheckBoxGroupValue } from './CheckboxGroup'
import { IS_WEB } from '../../constants'
import { useMediaQuerySize } from '../../hooks/useMediaQuerySize'
import { BodyTextSize, getFontStyles, StyledText, TextType } from '../../styles/typeStyles'

/**
 * This component is based on imageRadioGroup.tsx,  It handles logic to render list view or grid view for
 * Checkbox Group that has images in the buttons i.e. Primary Needs.
 */

export const ImageCheckboxGroupRFF: FunctionComponent<FieldRenderProps<CheckBoxGroupValue>> = ({
  input: { value, onChange, name },
  meta: { touched, error, active },
  label,
  labelSecondary,
  subLabel,
  largeSubLabel,
  labelAlignment,
  readOnly,
  options,
  buttonType: propButtonType = CheckboxButtonType.GRID,
  baseInputStyle,
  labelStyle,
  labelContainerStyle,
  inputContainerStyle,
  customTextStyle,
  noneOfTheAboveCheckboxId,
  limitSelected,
}) => {
  // When buttonType is GRID, change to to CIRCLE_TEXT_OUTLINE when text oveflows.

  // Beware! This is an incredibly hacky solution.
  // It relies on an initial duplicate rendering using dummy text components to measure the text overflow,
  // then sets curButtonType to the final buttonType.

  const [curButtonType, setCurButtonType] = useState<CheckboxButtonType | null>(
    propButtonType === CheckboxButtonType.GRID ? null : propButtonType,
  )

  const { isMinWidthLaptop, isMinWidthTablet } = useMediaQuerySize()
  const gridCheckboxSize = isMinWidthTablet ? GridCheckboxSize.DEFAULT : GridCheckboxSize.SMALL
  const gridButtonsGap = isMinWidthLaptop ? 16 : 8

  const [containerWidth, setContainerWidth] = useState(0)
  const onLayout = (e: LayoutChangeEvent) => {
    const { width } = e.nativeEvent.layout
    setContainerWidth(width)
    if (width !== containerWidth) {
      setCurButtonType(null)
    }
  }

  const { colors, fontFamilyMetadata } = useTheme()
  const buttonType = curButtonType ?? propButtonType
  //const checkboxButtonStyles = getCheckboxButtonStyles(colors)[buttonType]

  const maxLines = 2
  const maxTextWidth =
    containerWidth > 0
      ? getCheckboxButtonWidth(containerWidth + gridButtonsGap, gridButtonsGap) - 2 * ((isMinWidthLaptop ? 16 : 12) + 1)
      : 0 // padding horizontal + border
  const maxTextHeight =
    maxLines * getFontStyles(colors, fontFamilyMetadata)[TextType.BODY][BodyTextSize.DEFAULT].lineHeight

  // // When all entries are true, none of the text overflowed.
  const textFits: (null | true)[] = []
  textFits.length = options.length
  textFits.fill(null)
  function textDidNotOverflow(buttonIndex: number) {
    textFits[buttonIndex] = true
    if (textFits.every(Boolean)) {
      setCurButtonType(propButtonType)
    }
  }

  return (
    <View>
      {/* Dummy view that renders the text to measure its dimensions with onLayout */}
      <View
        style={{ width: maxTextWidth, position: 'absolute', opacity: 0, zIndex: -99, top: 0 }}
        aria-hidden={true}
        accessibilityHidden={true}
        importantForAccessibility={'no-hide-descendants'}
      >
        {(options as { text: string }[]).map(({ text }, index) => {
          // on mobile, onLayout can fire multiple times which makes the measurement inaccurate, so use onTextLayout
          // react-native-web does not support onTextLayout
          let onLayout: TextProps['onLayout']
          let onTextLayout: TextProps['onTextLayout']
          if (buttonType === CheckboxButtonType.GRID) {
            if (IS_WEB) {
              onLayout = (evt) => {
                if (maxTextWidth <= 0) {
                  return
                }
                const { width, height } = evt.nativeEvent.layout
                if (width > maxTextWidth + 1 || height > maxTextHeight + 1) {
                  setCurButtonType(CheckboxButtonType.CHECKBOX_TEXT_OUTLINE)
                } else {
                  textDidNotOverflow(index)
                }
              }
            } else {
              onTextLayout = (evt) => {
                const lines = evt.nativeEvent.lines
                if (lines.length > maxLines) {
                  // Text wrapped > 2 lines
                  setCurButtonType(CheckboxButtonType.CHECKBOX_TEXT_OUTLINE)
                } else {
                  textDidNotOverflow(index)
                }
              }
            }
          }
          return (
            <StyledText
              // Force text to re-mount to fire onLayout
              // Subsequent renders of ImageRadioGroup must re-check all text lengths and fill the textFits array
              key={[curButtonType, maxTextWidth, text].map(String).join('_')}
              style={{ flexShrink: 0, alignSelf: 'center', textAlign: 'center' }}
              type={TextType.BODY}
              size={BodyTextSize.DEFAULT}
              onLayout={onLayout}
              onTextLayout={onTextLayout}
            >
              {text}
            </StyledText>
          )
        })}
      </View>
      <View onLayout={onLayout} style={{ opacity: containerWidth === 0 || curButtonType === null ? 0 : undefined }}>
        <CheckboxGroup
          value={value}
          onChange={onChange}
          name={name}
          label={label}
          labelSecondary={labelSecondary}
          subLabel={subLabel}
          largeSubLabel={largeSubLabel}
          labelAlignment={labelAlignment}
          error={touched && error}
          options={options}
          readOnly={readOnly}
          buttonType={buttonType}
          active={active}
          baseInputStyle={baseInputStyle}
          labelStyle={labelStyle}
          labelContainerStyle={labelContainerStyle}
          inputContainerStyle={{
            ...(buttonType === CheckboxButtonType.CHECKBOX_TEXT_OUTLINE && { marginBottom: 8 }),
            ...inputContainerStyle,
          }}
          customTextStyle={customTextStyle}
          noneOfTheAboveCheckboxId={noneOfTheAboveCheckboxId}
          parentContainerWidth={containerWidth > 0 ? containerWidth : undefined}
          gridCheckboxSize={gridCheckboxSize}
          gridButtonsGap={gridButtonsGap}
          limitSelected={limitSelected}
          hideBaseInput={true}
        />
      </View>
    </View>
  )
}
